// The harness's default-resolving hook layer. The run query, the four run
// mutations, the pure refetch-cadence helpers and the run stream lifecycle all
// live in `/data` and take their app-shaped inputs as required parameters; this
// module is where those inputs get their defaults, and it keeps its full export
// surface so `src/features/agent-harness/index.ts`'s `export * from './hooks'`
// and every existing importer resolve unchanged through the delegate at the old
// path.
//
// THE THREE THINGS IT RESOLVES were each a named blocker on this family's
// `query` dimension, and each is asserted in `hooks.test.tsx` rather than
// described:
//
//   1. `agentHarnessClient` — the transport ASSEMBLY. `transport` is required
//      on the package hooks and the default is re-applied here, so the
//      one-argument `useQueryRun(observedRunId)` in `src/features/rpa/cua-status`
//      is unchanged.
//   2. `streamHeaders` — the SSE header factory. The fallback that
//      `RunTransport.streamHeaders` being OPTIONAL implies
//      (`transport.streamHeaders ?? streamHeaders`) is resolved here, which is
//      the only layer that can express it: a non-Scribe transport may omit the
//      factory, and the Scribe one is this surface's default.
//   3. The failed-cancel toast. It fired inside a per-call
//      `cancelMutate(id, { onError })`, not on a mutation definition, so `meta`
//      has no channel for it — putting it on `useMutateCancelTurn`'s meta would
//      toast for every consumer of that mutation instead of this one call site.
//      It is the `onCancelFailed` port, supplied below with byte-identical copy.
//
// The toast is why this module is in `/ui` and not `/data`: `@heidi/design-system`
// is exactly what a rendered surface is for, and it is what the headless zone
// may never import.
import { toast } from '@heidi/design-system/toast';
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { agentHarnessKeys } from '../../contracts';
import {
  computeRunRefetchInterval,
  isTerminalRunStatus,
  runQueryOptions,
  useMutateCancelTurn as useMutateCancelTurnCore,
  useMutateResolveApproval as useMutateResolveApprovalCore,
  useMutateSetApprovalMode,
  useMutateSubmitTurn as useMutateSubmitTurnCore,
  useQueryRun as useQueryRunCore,
  useRunStreamLifecycle as useRunStreamLifecycleCore,
} from '../../data';

import type { RunTransport } from '../../contracts';
import { agentHarnessClient } from './client';
import { streamHeaders } from './stream/stream-http';

// Imported AND re-exported, not re-declared: the key factory is frozen in
// the package's own contracts door, whose comment already specifies
// this shape. Re-declaring the literal here gave one cache identity two
// definition sites, which drift apart the moment either is edited — the hazard
// `key-single-site` exists to catch.
export { agentHarnessKeys };

// These take no app input at all, so they are plain re-exports.
export {
  computeRunRefetchInterval,
  isTerminalRunStatus,
  runQueryOptions,
  useMutateSetApprovalMode,
};

export type {
  RunStreamEventObserver,
  RunStreamLifecycleResult,
  TurnOperationHandle,
  TurnSubmissionHandle,
  UseRunStreamLifecycleOptions,
} from '../../data';

export const useQueryRun = (
  agentRunId: string | null | undefined,
  transport: RunTransport = agentHarnessClient
) => useQueryRunCore(agentRunId, transport);

export const useMutateSubmitTurn = (transport: RunTransport = agentHarnessClient) =>
  useMutateSubmitTurnCore(transport);

export const useMutateCancelTurn = (transport: RunTransport = agentHarnessClient) =>
  useMutateCancelTurnCore(transport);

export const useMutateResolveApproval = (transport: RunTransport = agentHarnessClient) =>
  useMutateResolveApprovalCore(transport);

/**
 * Copy for the one toast this root still raises. The descriptor sits at module
 * scope; the handler itself is built per hook instance below because the copy
 * needs the active locale's `intl`.
 */
const cancelFailedMessages = defineMessages({
  title: {
    defaultMessage: 'Could not stop the turn',
    id: 'HQ0qQrg3IW',
    description: 'Toast after a turn cancellation request fails',
  },
});

type CoreLifecycleOptions = Parameters<typeof useRunStreamLifecycleCore>[2];

export const useRunStreamLifecycle = (
  agentRunId: string | undefined,
  transport: RunTransport = agentHarnessClient,
  options: Omit<CoreLifecycleOptions, 'streamHeaders' | 'onCancelFailed'> = {}
) => {
  const intl = useIntl();
  // Stable per locale, so the object the package hook receives keeps a stable
  // identity — the cancel handler lists `options` in its dependency array,
  // exactly as the pre-carve body did.
  const onCancelFailed = useCallback(() => {
    toast({ title: intl.formatMessage(cancelFailedMessages.title), tone: 'destructive' });
  }, [intl]);

  return useRunStreamLifecycleCore(agentRunId, transport, {
    ...options,
    // The fallback `RunTransport.streamHeaders` being optional implies. A
    // non-Scribe transport may omit it; the Scribe factory is the default and it
    // may only be named here.
    streamHeaders: transport.streamHeaders ?? streamHeaders,
    onCancelFailed,
  });
};
